import { useId, type ComponentProps, type ReactElement, type ReactNode } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface AuthFieldProps extends ComponentProps<'input'> {
  label: string;
  error?: string;
  // Shown under the input while there is no error, e.g. the password rule.
  hint?: string;
  endAdornment?: ReactNode;
  // Rendered between the input and its message, e.g. a password strength meter.
  children?: ReactNode;
}

function AuthField({
  label,
  error,
  hint,
  endAdornment,
  children,
  id,
  className,
  ...inputProps
}: AuthFieldProps): ReactElement {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;
  const message = error ?? hint;

  return (
    <div className="vtwo:flex vtwo:flex-col vtwo:gap-2.5">
      <Label htmlFor={inputId} className="vtwo:leading-[normal] vtwo:text-auth-muted">
        {label}
      </Label>
      <div>
        <div className="vtwo:relative">
          <Input
            id={inputId}
            aria-invalid={Boolean(error)}
            aria-describedby={message ? messageId : undefined}
            className={cn(
              'vtwo:h-10 vtwo:rounded vtwo:border-auth-border vtwo:bg-auth-field vtwo:px-3 vtwo:text-sm vtwo:text-auth-foreground vtwo:shadow-none',
              'vtwo:focus-visible:border-auth-focus vtwo:focus-visible:ring-auth-focus/30',
              'vtwo:aria-invalid:border-auth-error vtwo:aria-invalid:ring-auth-error/20',
              'vtwo:read-only:text-auth-muted',
              // Keep the browser's autofill from repainting the field light.
              'vtwo:autofill:shadow-[inset_0_0_0_1000px_rgb(46,48,50)] vtwo:autofill:[-webkit-text-fill-color:var(--vtwo-color-auth-foreground)]',
              endAdornment && 'vtwo:pr-11',
              className
            )}
            {...inputProps}
          />
          {endAdornment && (
            <div className="vtwo:absolute vtwo:inset-y-0 vtwo:right-1 vtwo:flex vtwo:items-center">
              {endAdornment}
            </div>
          )}
        </div>
        {children}
        {message && (
          <p
            id={messageId}
            className={cn(
              'vtwo:mt-2.5 vtwo:text-xs vtwo:leading-[normal]',
              error ? 'vtwo:text-auth-error' : 'vtwo:text-auth-muted'
            )}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
}

export default AuthField;
